import { Component, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import {
    DeviceManagementConfigurationPolicy,
    DeviceManagementConfigurationPolicyAssignment,
    DeviceManagementConfigurationSetting,
} from '@microsoft/microsoft-graph-types-beta';
import { BaseComponent } from '../../../../base/base.component';
import * as DeviceConfigurationWindowsLAPS from 'projects/angular-clarity/src/app/modules/sway/tools/schemas/graph/intune/configuration-policy/windows-laps';
import {
    DeviceConfigurationWindowsLAPSSchema,
    LAPS_AD_PASSWORD_ENCRYPTION_OPTIONS,
    LAPS_AUTOMATIC_ACCOUNT_OPTIONS,
    LAPS_AUTO_ACCOUNT_ENABLE_ACCOUNT_OPTIONS,
    LAPS_AUTO_ACCOUNT_RANDOMIZE_NAME_OPTIONS,
    LAPS_AUTO_ACCOUNT_TARGET_OPTIONS,
    LAPS_BACKUP_DIRECTORY_OPTIONS,
    LAPS_COMPLEXITY_OPTIONS,
    LAPS_PASSWORD_EXPIRATION_PROTECTION_OPTIONS,
    LAPS_POST_AUTH_ACTION_OPTIONS,
} from 'projects/angular-clarity/src/app/modules/sway/tools/schemas/graph/intune/configuration-policy/windows-laps/model';
import {
    LapsCurrentSettings,
    readLapsCurrentSettingsFromData,
} from 'projects/angular-clarity/src/app/modules/sway/tools/schemas/graph/intune/configuration-policy/windows-laps/helper';

@Component({
    selector: 'app-baseline',
    templateUrl: './baseline.component.html',
    styleUrls: ['./baseline.component.scss', '../../../../styles/baseline-style.scss'],
})
export class BaselineComponent extends BaseComponent implements OnInit, OnChanges {
    tooltips = DeviceConfigurationWindowsLAPS.tooltips;
    complexityOptions = LAPS_COMPLEXITY_OPTIONS;
    backupDirectoryOptions = LAPS_BACKUP_DIRECTORY_OPTIONS;
    postAuthActionOptions = LAPS_POST_AUTH_ACTION_OPTIONS;
    automaticAccountOptions = LAPS_AUTOMATIC_ACCOUNT_OPTIONS;
    automaticAccountTargetOptions = LAPS_AUTO_ACCOUNT_TARGET_OPTIONS;
    automaticAccountRandomizeNameOptions = LAPS_AUTO_ACCOUNT_RANDOMIZE_NAME_OPTIONS;
    automaticAccountEnableAccountOptions = LAPS_AUTO_ACCOUNT_ENABLE_ACCOUNT_OPTIONS;
    adPasswordEncryptionOptions = LAPS_AD_PASSWORD_ENCRYPTION_OPTIONS;
    passwordExpirationProtectionOptions = LAPS_PASSWORD_EXPIRATION_PROTECTION_OPTIONS;

    current: LapsCurrentSettings = {
        backupDirectory: null,
        passwordAgeDays: null,
        passwordLength: null,
        passwordComplexity: null,
        administratorAccountName: null,
        postAuthenticationActions: null,
        postAuthenticationResetDelay: null,
        automaticAccountManagementEnabled: null,
        automaticAccountManagementTarget: null,
        automaticAccountManagementRandomizeName: null,
        automaticAccountManagementEnableAccount: null,
        automaticAccountManagementNameOrPrefix: null,
        passwordExpirationProtectionEnabled: null,
        adPasswordEncryptionEnabled: null,
        adPasswordEncryptionPrincipal: null,
        adEncryptedPasswordHistorySize: null,
    };

    policy: DeviceManagementConfigurationPolicy;
    assignments: DeviceManagementConfigurationPolicyAssignment[] = [];
    settings: DeviceManagementConfigurationSetting[] = [];

    constructor(private fb: FormBuilder) {
        super();
    }

    ngOnInit(): void {
        this.getData();
        const schema = JSON.parse(JSON.stringify(this.baseline.schema));

        this.initForm(schema);
        this.observeFormChange();
    }

    ngOnChanges(_changes: SimpleChanges): void {
        this.getData();
    }

    getData() {
        if (this.data.length > 0) {
            this.policy = this.data?.[0];
            this.assignments = this.policy?.assignments || [];
            this.settings = this.policy?.settings || [];

            this.current = readLapsCurrentSettingsFromData(this.settings);
        }
    }

    observeFormChange() {
        const schema: DeviceConfigurationWindowsLAPSSchema = JSON.parse(JSON.stringify(this.baseline.schema));

        this.form.valueChanges.subscribe(() => {
            const updatedSchema = DeviceConfigurationWindowsLAPS.updateSchema(this.formValue, schema);

            const noGroupAssignments = updatedSchema.contains.properties.assignments?.maxItems === 0;

            if (noGroupAssignments) {
                this.baselineErrorChange.next({ remediate: true, save: true });
            } else {
                this.baselineErrorChange.next({ remediate: false, save: false });
            }

            this.baselineChange.next({
                ...this.baseline,
                schema: JSON.parse(JSON.stringify(updatedSchema)),
            });
        });
    }

    initForm(schema: DeviceConfigurationWindowsLAPSSchema): void {
        this.form = DeviceConfigurationWindowsLAPS.initForm(schema, this.fb);
    }

    backupDirectoryLabel(value: number | null): string {
        if (value === null || value === undefined) return 'Not configured';
        return this.backupDirectoryOptions.find((option) => option.value === value)?.label || `${value}`;
    }

    complexityLabel(value: number | null): string {
        if (value === null || value === undefined) return 'Not configured';
        return this.complexityOptions.find((option) => option.value === value)?.label || `${value}`;
    }

    postAuthActionLabel(value: number | null): string {
        if (value === null || value === undefined) return 'Not configured';
        return this.postAuthActionOptions.find((option) => option.value === value)?.label || `${value}`;
    }

    autoAccountTargetLabel(value: number | null): string {
        if (value === null || value === undefined) return 'Not configured';
        return this.automaticAccountTargetOptions.find((option) => option.value === value)?.label || `${value}`;
    }

    /** Map a current value to the same label its desired dropdown shows (single source of truth). */
    optionLabel(options: { value: unknown; label: string }[], value: unknown): string {
        if (value === null || value === undefined) return 'Not configured';
        return options.find((option) => option.value === value)?.label ?? 'Not configured';
    }

    /**
     * Deviation status for an optional setting.
     * - When the desired baseline leaves it "not configured", the setting must be ABSENT from the policy, so
     *   any current value is a deviation.
     * - Otherwise it deviates when the current value differs from the desired one.
     * Empty string / null / undefined are all treated as "unset" so they compare equal.
     */
    statusError(configured: boolean, current: unknown, desired: unknown): boolean {
        const unset = (value: unknown): boolean => value === null || value === undefined || value === '';
        if (!configured) return !unset(current);
        if (unset(current) && unset(desired)) return false;
        return current !== desired;
    }

    get desiredSettingsControl(): FormGroup {
        return this.form.get('settings') as FormGroup;
    }

    get desiredSettingsValue() {
        return this.desiredSettingsControl?.value || {};
    }

    /** Selected backup directory coerced to a number (0 = Disabled, 1 = Entra ID, 2 = Active Directory). */
    get desiredBackupDirectory(): number {
        const value = this.desiredSettingsValue?.backupDirectory;
        return value === null || value === undefined ? 0 : Number(value);
    }

    get desiredPolicyControl(): FormGroup {
        return this.form.get('policy') as FormGroup;
    }

    get desiredPolicyValue() {
        return this.desiredPolicyControl.value;
    }

    get formValue() {
        return this.form.getRawValue();
    }
}
